/**
 * Image → Pixel Art quantizer (pure, framework-free, unit-testable).
 *
 * Takes already-downsampled RGBA pixel data (W×H, the target pixel-art grid)
 * and produces a compact `palette` (hex) + `cells` (Uint16Array of palette
 * indices, 0 == transparent) ready to feed the PixelArt model / RLE encoder.
 *
 * Colour reduction uses median-cut; optional Floyd–Steinberg dithering diffuses
 * quantisation error for smoother gradients. Pixels whose source alpha is below
 * `alphaThreshold` become transparent (index 0) so the territory shows through.
 */

export interface PixelizeOpts {
  maxColors: number;     // 2..256
  dither: boolean;
  alphaThreshold: number; // 0..255 — source alpha below this => transparent
}

export interface PixelizeResult {
  palette: string[];      // hex #rrggbb
  cells: Uint16Array;     // length W*H; 0 = transparent, i>=1 => palette[i-1]
}

interface RGB { r: number; g: number; b: number }

function clampByte(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : Math.round(v);
}

function toHex(r: number, g: number, b: number): string {
  const h = (n: number) => clampByte(n).toString(16).padStart(2, "0");
  return `#${h(r)}${h(g)}${h(b)}`;
}

function channelRange(box: RGB[]): RGB {
  let rmin = 255, gmin = 255, bmin = 255, rmax = 0, gmax = 0, bmax = 0;
  for (const p of box) {
    if (p.r < rmin) rmin = p.r; if (p.r > rmax) rmax = p.r;
    if (p.g < gmin) gmin = p.g; if (p.g > gmax) gmax = p.g;
    if (p.b < bmin) bmin = p.b; if (p.b > bmax) bmax = p.b;
  }
  return { r: rmax - rmin, g: gmax - gmin, b: bmax - bmin };
}

function averageColor(box: RGB[]): RGB {
  let r = 0, g = 0, b = 0;
  for (const p of box) { r += p.r; g += p.g; b += p.b; }
  const n = Math.max(1, box.length);
  return { r: r / n, g: g / n, b: b / n };
}

/** Median-cut colour quantisation → up to `max` representative colours. */
export function medianCut(pixels: RGB[], max: number): RGB[] {
  if (pixels.length === 0) return [];
  const target = Math.max(1, Math.min(256, Math.floor(max)));
  const boxes: RGB[][] = [pixels];
  while (boxes.length < target) {
    let splitIdx = -1;
    let widest = 0;
    for (let i = 0; i < boxes.length; i++) {
      if (boxes[i].length < 2) continue;
      const rng = channelRange(boxes[i]);
      const m = Math.max(rng.r, rng.g, rng.b);
      if (m > widest) { widest = m; splitIdx = i; }
    }
    if (splitIdx < 0) break; // every box is a single colour
    const box = boxes[splitIdx];
    const rng = channelRange(box);
    const ch: keyof RGB = rng.r >= rng.g && rng.r >= rng.b ? "r" : rng.g >= rng.b ? "g" : "b";
    box.sort((a, b) => a[ch] - b[ch]);
    const mid = box.length >> 1;
    boxes.splice(splitIdx, 1, box.slice(0, mid), box.slice(mid));
  }
  return boxes.map(averageColor);
}

function nearest(c: RGB, palette: RGB[]): number {
  let best = 0, bestD = Infinity;
  for (let i = 0; i < palette.length; i++) {
    const p = palette[i];
    const dr = c.r - p.r, dg = c.g - p.g, db = c.b - p.b;
    const d = dr * dr + dg * dg + db * db;
    if (d < bestD) { bestD = d; best = i; }
  }
  return best;
}

export function pixelize(
  data: Uint8ClampedArray | number[],
  width: number,
  height: number,
  opts: PixelizeOpts,
): PixelizeResult {
  const n = width * height;
  const cells = new Uint16Array(n);
  const opaque = new Uint8Array(n);
  const samples: RGB[] = [];
  const sampleOf = new Int32Array(n).fill(-1);
  const threshold = Math.max(0, Math.min(255, opts.alphaThreshold));

  for (let i = 0; i < n; i++) {
    const a = data[i * 4 + 3];
    if (a <= threshold) continue; // transparent
    opaque[i] = 1;
    samples.push({ r: data[i * 4], g: data[i * 4 + 1], b: data[i * 4 + 2] });
    sampleOf[i] = samples.length - 1;
  }

  if (samples.length === 0) {
    // fully transparent image → keep a single (unused) palette colour so the
    // model stays valid (palette length must be >= 1).
    return { palette: ["#000000"], cells };
  }

  const palRGB = medianCut(samples.map((s) => ({ ...s })), opts.maxColors);
  const palette = palRGB.map((c) => toHex(c.r, c.g, c.b));

  if (!opts.dither) {
    for (let i = 0; i < n; i++) {
      if (!opaque[i]) continue;
      cells[i] = nearest(samples[sampleOf[i]], palRGB) + 1;
    }
    return { palette, cells };
  }

  // Floyd–Steinberg error diffusion over a float working buffer.
  const buf = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    if (!opaque[i]) continue;
    buf[i * 3] = data[i * 4];
    buf[i * 3 + 1] = data[i * 4 + 1];
    buf[i * 3 + 2] = data[i * 4 + 2];
  }
  const spread = (i: number, er: number, eg: number, eb: number, f: number) => {
    if (i < 0 || i >= n || !opaque[i]) return;
    buf[i * 3] += er * f; buf[i * 3 + 1] += eg * f; buf[i * 3 + 2] += eb * f;
  };
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = y * width + x;
      if (!opaque[i]) continue;
      const cur: RGB = { r: buf[i * 3], g: buf[i * 3 + 1], b: buf[i * 3 + 2] };
      const pi = nearest(cur, palRGB);
      cells[i] = pi + 1;
      const pc = palRGB[pi];
      const er = cur.r - pc.r, eg = cur.g - pc.g, eb = cur.b - pc.b;
      if (x + 1 < width) spread(i + 1, er, eg, eb, 7 / 16);
      if (y + 1 < height) {
        if (x > 0) spread(i + width - 1, er, eg, eb, 3 / 16);
        spread(i + width, er, eg, eb, 5 / 16);
        if (x + 1 < width) spread(i + width + 1, er, eg, eb, 1 / 16);
      }
    }
  }
  return { palette, cells };
}

/**
 * Grid dimensions that preserve the source aspect ratio, with the long side
 * fixed to `longSide` (clamped 1..maxDim). Never deforms the image.
 */
export function fitGridToAspect(
  imgW: number,
  imgH: number,
  longSide: number,
  maxDim = 256,
): { w: number; h: number } {
  const L = Math.max(1, Math.min(maxDim, Math.floor(longSide)));
  if (imgW <= 0 || imgH <= 0) return { w: L, h: L };
  let w: number, h: number;
  if (imgW >= imgH) { w = L; h = Math.round((L * imgH) / imgW); }
  else { h = L; w = Math.round((L * imgW) / imgH); }
  w = Math.max(1, Math.min(maxDim, w));
  h = Math.max(1, Math.min(maxDim, h));
  return { w, h };
}
